import { ChronocodeCmd, FindMeasureCommand, FindStyleCommand, FreedataCall, PermissionAccess, PermissionVisibility, StaticFeatureEnum, StyleLineVO, StyleVO } from '@wizeoo/akm-common-lib';
import { BATCH_KIND } from '@wizeoo/akm-common-lib';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { WizMathUtils, WIZ_EVENT_TYPE,  
  CONFIRMATION_MODE, CONFIRMATION_RESULT } from '@wizeoo/akm-core-lib';
import {
  ConfirmationResultObj,
  FilterComponent,
  WizDatasource,
  WizGroupCommand,
  WizList,
  WizPopUp,
  WizRoot,
  WizTextInput,
  WizTranslationBasic,
  WizValidationError,
  WizValidationErrors,
  WizValidatorFn,
  WizVgroup,
  WizIcon,
  BATCH_FILE_FORMAT,
  WizEventManager, WizEvent
} from '@wizeoo/akm-component-lib';
import { MultiDisplay, MultiDisplayConfig } from '@wizeoo/akm-common-lib';
import { AlkEvent } from '@wizeoo/akm-common-lib';
import { ALK_EVENT_TYPE } from '@wizeoo/akm-common-lib';
import { Model } from 'src/app/model/model';
import { ViewData } from '@wizeoo/akm-common-lib';
import { BaseView } from '@wizeoo/akm-common-lib';
import { ConditionRenderer } from '@wizeoo/akm-common-lib';

/**
 * Vue de gestion des includes / excludes
 * Author LMA
 */
@Component({
  selector: 'style-view',
  templateUrl: './style.view.html',
})
export class StyleView extends BaseView {
  @ViewChild('mdMain') mdMain: MultiDisplay<StyleVO>; // grid principal

  @ViewChild('tiCode', { read: WizTextInput }) tiCode: WizTextInput; // champ de saisie du code
  @ViewChild('tiVisionCode', { read: WizTextInput }) tiVisionCode: WizTextInput; // champ d'affichage de la vision
  /** Composant de saisie de la description */
  @ViewChild('trsDesc', { read: WizTranslationBasic }) trsDesc: WizTranslationBasic;
  @ViewChild('lstStyles', { read: WizList }) lstStyles: WizList; // liste des conditions

  @ViewChild('vgDemo', { read: WizVgroup }) vgDemo: WizVgroup; // blah blah
  @ViewChild('vgEdition', { read: WizVgroup }) vgEdition: WizVgroup; // ensemble de la zone de détail pour écoute ModelUpdate

  /** Button d'ajout */
  @ViewChild('btnCreate', { read: WizIcon, static: false }) btnCreate: WizIcon;
  /** Bouton d'export */
  @ViewChild('btnExport', { read: WizIcon, static: false }) btnExport: WizIcon;
  /** Bouton d'import */
  @ViewChild('btnImport', { read: WizIcon, static: false }) btnImport: WizIcon;
  /** Bouton de suppression */
  @ViewChild('btnRemove', { read: WizIcon, static: false }) btnRemove: WizIcon;

  public lineRenderer: any = ConditionRenderer; // rendu des lignes de conditions

  public currentStyle: StyleVO = null; // sélection active (objet métier vrai)
  public clone: StyleVO = null; // clone de travail

  /** Constructeur */
  constructor(public elementRef: ElementRef) {
    super(elementRef);
  }

  /** DS du grid principal */
  public mainDS: WizDatasource<StyleVO> = new WizDatasource<StyleVO>([])
    .setSortFields([{ property: 'code', way: 'asc', numeric: false }])
    .setSelectionMode('single')
    .build();

  /** DS des conditions */
  public conditionDS: WizDatasource<StyleLineVO> = new WizDatasource<StyleLineVO>([])
    .setSortFields([{ property: 'order', way: 'asc', numeric: true }])
    .setSelectionMode('none')
    .build();

  /** Configuration du mode multi display (principal) */
  public mainConfig: MultiDisplayConfig<StyleVO> = {
    model: Model.getInstance(),
    datasource: this.mainDS,
    defaultMode: 'grid',
    onSelectionChanging: this.askSelectMain.bind(this),
    grid: {
      enabled: true,
      columns: [
        { headerName: '$.common.code.$', width: 80, sortable: true, field: 'code', tooltipField: 'code' },
        { headerName: '$.style.formula.$', flex: 1, sortable: true, field: 'formula', tooltipField: 'formula' },
      ],
    },
    filter: {
      enabled: true,
      initFilter: this.initMainFilter.bind(this),
    },
  };

  /* #############################################################
                      CYCLE DE VIE
     ############################################################# */

  /**
   * Pretraitement: chargement des données nécessaires à la vue
   * @return WizGroupCommand
   */
  public wizBeforeInit(): WizGroupCommand {
    super.wizBeforeInit();
    const commands = [];
    // disponibilité des chronocodes pour labels
    if (!Model.getInstance().chronocodeEngine.getChronocodesMap()) {
      commands.push(new ChronocodeCmd(Model.getInstance()).find());
    }
    // disponibilité des freedatas pour labels
    if (!Model.getInstance().freedataEngine.getFreedataMap()) {
      commands.push(new FreedataCall(Model.getInstance()));
    }
    // chargement systématique de la liste des mesures
    if (!Model.getInstance().measureList) {
      commands.push(new FindMeasureCommand(Model.getInstance()));
    }
    
    // chargement de la liste des styles
    if (!Model.getInstance().styleList) {
      commands.push(new FindStyleCommand(Model.getInstance()));
    }

    if (commands.length > 0) {
      return new WizGroupCommand().configure(commands, null, false);
    } else {
      return null;
    }
  }

  /**
   * Synchronisation des données
   */
  public wizSynchronizeData(): void {
    super.managePermissions([
      {
        feature: StaticFeatureEnum.STYLE,
        permissions: [
          {
            component: this.btnCreate,
            accesses: [PermissionAccess.CREATE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnRemove,
            accesses: [PermissionAccess.DELETE],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnExport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
          {
            component: this.btnImport,
            accesses: [PermissionAccess.BATCH],
            visibility: PermissionVisibility.DISABLED,
          },
        ],
      },
    ]);

    this.mainFilter.configure(['code', 'formula'], null, null); // alimentation du filtre principal
    this.mainDS.setSource(Model.getInstance().styleList); // alimentation de la liste principale
    this.tiCode.wizRestrict = ViewData.CODE_REGEXP;
    // TODO collecte du contexte pour currentStyle
    this.feed();
  }

  /** alimentation de la zone de détail */
  private feed(): void {
    if (!!this.clone) {
      this.tiCode.wizValue = this.clone.code; // alimentation du code
      this.tiVisionCode.wizValue = this.clone.visionCode; // alimentation du champ de vision
      this.trsDesc.translationData = Model.getInstance().localeEngine.buildTranslationData(this.clone.descriptionVO);

      this.vgEdition.visible = true;
      this.vgDemo.visible = false;

      if (this.clone.exist) {
        this.tiCode.wizDisabled = true;
      } else {
        this.tiCode.wizDisabled = false;
        setTimeout(() => {
          // sinon, pas le temps de rendre les compos enabled
          this.tiCode.wizFocus();
        }, 0);
      }

      this.conditionDS.setSource(this.clone.linesVO);
      this.lstStyles.wizDataProvider = this.conditionDS;
    } else {
      this.vgEdition.visible = false;
      this.vgDemo.visible = true;
    }

  }

  /** Mise en place des ecouteurs de la vue */
  public wizAddListener(): void {
    super.wizAddListener();
    WizEventManager.addListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE, this.updateModel); // écouteur global change pour modelUpdate
    WizEventManager.addListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_UP, this.switchConditions);
    WizEventManager.addListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_DOWN, this.switchConditions);
    WizEventManager.addListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_DELETE, this.deleteConditions);
    WizEventManager.addListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED, this.markForUpdate); // modif sur un élément de condition
    WizEventManager.addListener(this, WizRoot.root(), ALK_EVENT_TYPE.BATCH_IMPORT_RETURN, this.backFromImport);
  }

  /**
   * Suppression des ecouteurs de la vue
   */
  public wizRemoveListener(): void {
    super.wizRemoveListener();
    WizEventManager.removeListener(this, this.vgEdition, WIZ_EVENT_TYPE.MODEL_UPDATE);
    WizEventManager.removeListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_UP);
    WizEventManager.removeListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_DOWN);
    WizEventManager.removeListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_STEP_DELETE);
    WizEventManager.removeListener(this, this.lstStyles, ALK_EVENT_TYPE.STYLE_ELEMENT_CHANGED);
    WizEventManager.removeListener(this, WizRoot.root(), ALK_EVENT_TYPE.BATCH_IMPORT_RETURN);
  }

  /** modification du contenu d'une condition */
  private markForUpdate(e: AlkEvent): void {
    this.clone.maintenanceFlag = WizMathUtils.createUUID(); // forcer l'update sur modif de children
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

  /** suppression d'un élément de la liste */
  private deleteConditions(e: AlkEvent): void {
    const toDie: StyleLineVO = e.data;
    this.clone.removeLine(toDie);
    this.conditionDS.removeItem(toDie);
    let idx: number = 0;
    // recalcul des positions
    this.clone.linesVO
      .sort((a, b) => a.order - b.order)
      .forEach((line: StyleLineVO) => {
        line.order = idx;
        idx++;
      });
    this.clone.maintenanceFlag = WizMathUtils.createUUID(); // forcer l'update sur modif de children
    ViewData.wizUpdateModel = true;
  }

  /** permutation de 2 conditions */
  private switchConditions(e: AlkEvent): void {
    const me: StyleLineVO = e.data;
    const targetoffset: number = e.type === ALK_EVENT_TYPE.STYLE_STEP_UP ? -1 : +1;
    const other: StyleLineVO = this.clone.getLineByIdx(me.order + targetoffset);
    other.order = me.order;
    me.order = me.order + targetoffset;
    ViewData.wizUpdateModel = true;
    this.conditionDS.refresh();
    this.clone.maintenanceFlag = WizMathUtils.createUUID(); // forcer l'update sur modif de children
  }

  /** desactivation des boutons save et cancel sur updateModel */
  public disableButtons(): boolean {
    let result: boolean = true;
    if (this.clone && this.clone.code) {
      result = false;
    }
    if (!result) {
      result = !ViewData.wizUpdateModel;
    }
    return result;
  }

  /** desactivation des boutons save et cancel sur updateModel */
  public disableCancel(): boolean {
    return !ViewData.wizUpdateModel;
  }

  /* #############################################################
                  gestion sélection et filtres
     ############################################################# */

  private mainFilter: FilterComponent; // filterCompo du mdMain
  /** init du filter du mdMain */
  private initMainFilter(pFilter: FilterComponent): FilterComponent {
    this.mainFilter = pFilter;
    return this.mainFilter;
  }

  /** demande de changement de sélection */
  private askSelectMain(e: WizEvent = null): void {
    const vo: StyleVO = e.value;
    if (ViewData.wizUpdateModel) {
      e.preventDefault(); // sélection pas self && modifs en cours
      WizPopUp.confirm(
        '$.common.pending.update.title.$',
        '$.common.pending.update.detail.$',
        this.confirmSelect.bind(this),
        CONFIRMATION_MODE.CONFIRM_CANCEL,
        vo,
        null,
        null
      ).show();
    } else {
      if (!this.mainDS.isSelected(vo)) {
        // pas encore sélectionné
        this.doSelect(vo);
      } else {
        // déjà sélectionné, déselection
        this.doSelect(null);
      }
    }
  }

  /**
   * Confirmation de la sélection du style
   */
  private confirmSelect(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      const vo: StyleVO = result.data;
      this.mainDS.toggle(vo); // on a prevent l'event, on joue l'action
      this.doSelect(this.mainDS.selectedItem);
    }
  }

  /**
   * Changement de style sélectionné
   * @param pEvent WizEvent
   */
  private doSelect(vo: StyleVO) {
    ViewData.wizUpdateModel = false;
    if (vo) {
      this.currentStyle = vo;
      this.clone = Model.getInstance().styleEngine.clone(this.currentStyle);
    } else {
      this.currentStyle = null;
      this.clone = null;
    }
    this.feed();
  }

  /** création nouveau style */
  public askForAdd(): void {
    this.currentStyle = Model.getInstance().styleEngine.buildFake();
    this.clone = Model.getInstance().styleEngine.clone(this.currentStyle);
    this.mainDS.clearSelection();
    this.feed();
  }

  /** demande de suppression */
  public askForDelete(): void {
    WizPopUp.confirm(
      '$.common.delete.title.$',
      '$.common.delete.detail.$',
      this.doRemove.bind(this),
      CONFIRMATION_MODE.CONFIRM_CANCEL,
      this.currentStyle
    ).show();
  }

  /**
   * Suppression
   * @param result Objet de retour sur popUp de confirmation. Contient l'action choisie, et la data d'origine
   */
  private doRemove(result: ConfirmationResultObj): void {
    if (result.result === CONFIRMATION_RESULT.CONFIRM) {
      Model.getInstance().styleEngine.remove(this.clone, this.currentStyle, this.backFromMerge.bind(this));
    }
  }

  /**
   * Validateur d'unicité du code / shortCode
   */
  public uniqueCodeValidator(): WizValidatorFn {
    return (): WizValidationErrors | null => {
      // Pas de gestion d'erreur si le _clone est null
      if (!this.clone) {
        return null;
      }
      if (this.tiCode.wizValue !== null && this.tiCode.wizValue && this.tiCode.wizValue.length > 0) {
        // CHRONO = mot réservé
        if (this.tiCode.wizValue.toUpperCase() === 'CHRONO') {
          const temp: WizValidationError = {
            wizError: '$.common.keyword.$',
            wizErrorParams: [],
          };
          return [temp];
        }

        // Recherche d'une autre style avec le même code
        let otherStyle: StyleVO = null;
        if (Model.getInstance().styleMap.has(this.clone.visionCode)) {
          // check map existe
          otherStyle = Model.getInstance().styleMap.get(this.clone.visionCode).get(this.tiCode.wizValue.toUpperCase());
        }

        // Si un style existe deja avec le meme code on affiche le message
        if (otherStyle && otherStyle.uid !== this.clone.uid) {
          const temp: WizValidationError = {
            wizError: '$.style.error.code.unique.$',
            wizErrorParams: [],
          };
          return [temp];
        }
      }
      return null;
    };
  }

  /** ajout d'une condition au style */
  public askForAddLine(): void {
    const newLine: StyleLineVO = Model.getInstance().styleEngine.buildFakeLine(this.clone);
    this.clone.addLine(newLine);
    this.conditionDS.addItem(newLine);
  }

  /**
   * modification d'un élément de modèle
   * @param e WizEvent
   */
  private updateModel(e: WizEvent): void {
    switch (e.component) {
      case this.tiCode:
        this.clone.code = this.tiCode.wizValue?.toUpperCase();
        break;
      case this.trsDesc:
        this.clone.descriptionVO.updateTranslationEntry(e.value);
        this.clone.maintenanceFlag = WizMathUtils.createUUID(); // PATCH MAJ SUBCOLL
        break;
      // default: // interception des mauvais update
      //   return;
    }
    this.clone.maintenanceFlag = WizMathUtils.createUUID(); // forcer l'update sur modif de children
    WizEventManager.dispatchEvent(this.elementRef, new AlkEvent(ALK_EVENT_TYPE.UPDATE_MODEL, true));
  }

  /** persistence sur save */
  public doMerge(): void {
    Model.getInstance().styleEngine.merge(this.clone, this.currentStyle, this.backFromMerge.bind(this));
  }

  /** cancel */
  public doCancel(): void {
    this.clone = Model.getInstance().styleEngine.clone(this.currentStyle);
    this.feed();
  }

  /**
   * Callback sur retour de persistence
   * @param bddBack style créé ou modifié ou supprimé
   * @param operation CUD
   */
  private backFromMerge(): void {
    this.mainDS.refresh();
    // this.mainDS.clearSelection();
    // this.currentStyle = null;
    // this.clone = null;
  }

  public doAct(action: string): void {
    switch (action) {
      case 'export':
        Model.getInstance().batchEngine.export(BATCH_KIND.STYLE, BATCH_FILE_FORMAT.JSON);
        break;
      case 'import':
        Model.getInstance().batchEngine.import(BATCH_KIND.STYLE);
        break;
    }
  }

  /** callback sur retour d'import : recharger les Freedatas */
  private backFromImport(e: AlkEvent): void {
    if (e && e.data === BATCH_KIND.STYLE) {
      new FindStyleCommand(Model.getInstance()).configure(this.backFromReload.bind(this)).execute();
    }
  }

  private backFromReload(e: any): void {
    this.wizSynchronizeData();
  }

  /**
   * Est-ce que l'utilisateur peut créer ?
   * @returns 
   */
  public get cantCreate(): boolean {
    return !Model.getInstance().permissionEngine.canCreate(StaticFeatureEnum.STYLE);
  }

  /**
   * Est-ce que l'utilisateur peut mettre à jour ?
   * @returns 
   */
  public get cantUpdate(): boolean {
    return !Model.getInstance().permissionEngine.canUpdate(StaticFeatureEnum.STYLE);
  }

  /**
   * Est-ce que l'utilisateur peut mettre à jour ?
   * @returns 
   */
  public get cantDelete(): boolean {
    return !Model.getInstance().permissionEngine.canDelete(StaticFeatureEnum.STYLE);
  }
}
